import { createFixedPositionMarketingTiles } from '../utils/create-fixed-position-marketing-tiles';
import { FLEXCAR_IS_TILES } from '../constants/flexcar-is-tiles';
import { random, shuffle } from 'lodash-es';
import { ReactNode, useMemo, useState, useEffect } from 'react';
import { TESTIMONIALS } from '@/homepage/constants/testimonials';
import LowInventoryTile from '../components/marketing-tiles/low-inventory-tile';
import TestimonialTile from '../components/marketing-tiles/testimonial-tile';
import useDoesSupportWebp from '@/common/hooks/use-does-support-webp';
import WaitlistTile from '../components/marketing-tiles/waitlist-tile';

/** Minimum car tiles allowed between marketing tiles */
export const CARS_BETWEEN_MARKETING_TILE_ON_DESKTOP_MIN = 5;
export const CARS_BETWEEN_MARKETING_TILE_ON_MOBILE_MIN = 4;

/** Maximum car tiles allowed between marketing tiles */
export const CARS_BETWEEN_MARKETING_TILE_ON_DESKTOP_MAX = 6;
export const CARS_BETWEEN_MARKETING_TILE_ON_MOBILE_MAX = 6;

/** Position spotlight marketing tile in 3rd position on inventory */
export const SPOTLIGHT_TILE_INDEX_POSITION_ON_DESKTOP = 1;
export const SPOTLIGHT_TILE_INDEX_POSITION_ON_MOBILE = 1;

/**
 * Randomize between min and max, adding previous result to min to ensure we always have correct number of cars before new random
 * @param cars_between_marketing_tile_min minimum number of cars between marketing tiles
 * @param car_between_marketing_tile_max maximum number car tiles allowed between marketing tiles
 * @param previousMarketingTileIndex
 * @param isClientMounted
 * @returns index of marketing tile
 */
function randomizeBetweenMinAndMax(
  cars_between_marketing_tile_min: number,
  car_between_marketing_tile_max: number,
  previousMarketingTileIndex: number,
  isClientMounted: boolean
): number {
  if (previousMarketingTileIndex === 0) {
    return cars_between_marketing_tile_min - 1;
  }

  const min = previousMarketingTileIndex + cars_between_marketing_tile_min;
  const max = previousMarketingTileIndex + car_between_marketing_tile_max;

  return isClientMounted ? random(min, max) : min;
}

export interface MarketingTile {
  indexes: {
    mobile: number;
    desktop: number;
  };
  tile: ReactNode;
}

interface MarketingTilesProps {
  PUBLIC_CDN_URL: string;
  vehicleWaitlistEnabled?: boolean;
  gasSaverCents: string;
  vehiclesLength?: number;
}

export default function useGetMarketingTiles({
  PUBLIC_CDN_URL,
  vehicleWaitlistEnabled = false,
  gasSaverCents,
  vehiclesLength,
}: MarketingTilesProps): MarketingTile[] {
  const doesSupportWebp = useDoesSupportWebp();
  const [isClientMounted, setIsClientMounted] = useState(false);

  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  return useMemo(() => {
    if (!vehiclesLength) return [];

    // Low inventory tile
    if (vehiclesLength < CARS_BETWEEN_MARKETING_TILE_ON_DESKTOP_MIN) {
      return [
        {
          tile: vehicleWaitlistEnabled ? (
            <WaitlistTile
              PUBLIC_CDN_URL={PUBLIC_CDN_URL}
              doesSupportWebp={doesSupportWebp}
            />
          ) : (
            <LowInventoryTile />
          ),
          indexes: {
            mobile: vehiclesLength - 1,
            desktop: vehiclesLength - 1,
          },
        } as MarketingTile,
      ];
    }

    // Get fixed tiles and their last indices
    const { fixedTiles, lastDesktopIndex, lastMobileIndex } =
      createFixedPositionMarketingTiles({
        PUBLIC_CDN_URL,
        doesSupportWebp,
        gasSaverCents,
      });

    // Shuffle remaining tiles only if client mounted
    const tilesToShuffle = [
      ...FLEXCAR_IS_TILES,
      <TestimonialTile testimonial={TESTIMONIALS[0]} key="testimonials-sarah" />,
    ];

    const shuffledTiles = isClientMounted ? shuffle(tilesToShuffle) : tilesToShuffle;

    // Generate indices for shuffled tiles
    const randomTilesResult = shuffledTiles.reduce<{
      tiles: MarketingTile[];
      previousDesktopIndex: number;
      previousMobileIndex: number;
    }>(
      (acc, tile) => {
        const desktopIndex = randomizeBetweenMinAndMax(
          CARS_BETWEEN_MARKETING_TILE_ON_DESKTOP_MIN,
          CARS_BETWEEN_MARKETING_TILE_ON_DESKTOP_MAX,
          acc.previousDesktopIndex,
          isClientMounted
        );

        const mobileIndex = randomizeBetweenMinAndMax(
          CARS_BETWEEN_MARKETING_TILE_ON_MOBILE_MIN,
          CARS_BETWEEN_MARKETING_TILE_ON_MOBILE_MAX,
          acc.previousMobileIndex,
          isClientMounted
        );

        return {
          // Add new tile to accumulated tiles
          tiles: [
            ...acc.tiles,
            {
              tile,
              indexes: {
                mobile: mobileIndex,
                desktop: desktopIndex,
              },
            },
          ],
          // Update indices for next iteration
          previousDesktopIndex: desktopIndex,
          previousMobileIndex: mobileIndex,
        };
      },
      // Initialize with last fixed position tile indices
      {
        tiles: [],
        previousDesktopIndex: lastDesktopIndex,
        previousMobileIndex: lastMobileIndex,
      }
    );

    // Combine fixed tiles and random tiles
    const allTiles = [...fixedTiles, ...randomTilesResult.tiles];

    // Add waitlist tile at the end if enabled
    if (vehicleWaitlistEnabled) {
      allTiles.push({
        tile: (
          <WaitlistTile
            key="waitlist-tile"
            PUBLIC_CDN_URL={PUBLIC_CDN_URL}
            doesSupportWebp={doesSupportWebp}
          />
        ),
        indexes: {
          mobile: vehiclesLength - 1,
          desktop: vehiclesLength - 1,
        },
      });
    }

    return allTiles;
  }, [
    PUBLIC_CDN_URL,
    doesSupportWebp,
    gasSaverCents,
    vehiclesLength,
    vehicleWaitlistEnabled,
    isClientMounted,
  ]);
}
